import { NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, THEMES } from '../context/ThemeContext';
import { useState, useEffect } from 'react';

const VER = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const NAV = [
  { to:'/dashboard',    label:'Дашборд',    icon:'○' },
  { to:'/measurements', label:'Замеры',     icon:'◎' },
  { to:'/progress',     label:'Прогресс',   icon:'◈' },
];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@600;700;800;900&family=Inter:wght@400;500;600&display=swap');
  @keyframes _glow  { 0%,100%{box-shadow:0 0 16px var(--acc-glow)} 50%{box-shadow:0 0 30px var(--acc-glow)} }
  @keyframes _in    { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
  @keyframes _spin  { to{transform:rotate(360deg)} }
  @keyframes _pulse { 0%,100%{opacity:.6} 50%{opacity:1} }
  ._page { animation:_in .35s cubic-bezier(.16,1,.3,1) both }
  ._logo-glow { animation:_glow 3s ease-in-out infinite }
  ._nav-item { transition:all .15s ease; border-left:3px solid transparent; }
  ._nav-item:hover { background:var(--nav-hover) !important; padding-left:15px !important; }
  ._nav-active { background:var(--nav-active) !important; border-left-color:var(--acc) !important; color:var(--acc) !important; font-weight:700 !important; }
  ._color-btn { transition:all .2s ease; }
  ._color-btn:hover { transform:scale(1.25); }
  ._card { transition:transform .2s ease, box-shadow .2s ease; }
  ._card:hover { transform:translateY(-2px); box-shadow:0 8px 32px var(--card-shadow) !important; }
  ._btn { transition:all .2s ease; position:relative; overflow:hidden; }
  ._btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px var(--acc-glow) !important; }
  ._btn:active { transform:scale(.97); }
`;

function Sidebar({ onClose }) {
  const { user, logout, isAdmin } = useAuth();
  const { isDark, toggleDark, theme, setColor } = useTheme();
  const navigate  = useNavigate();
  const location  = useLocation();

  const acc  = theme.accent;
  const glow = theme.glow;
  const grad = theme.grad;

  // Light & dark tokens
  const T = isDark ? {
    bg:'#0D0B1A', border:'rgba(255,255,255,.07)', text:'#F2EEF9',
    muted:'rgba(200,185,220,.6)', div:'rgba(255,255,255,.06)',
    navHover:'rgba(255,255,255,.05)', navActive:`${acc}18`,
    profileBg:'rgba(255,255,255,.04)', profileBorder:`${acc}30`,
    cardShadow:'rgba(0,0,0,.4)',
  } : {
    bg:'#FDFBFF', border:'rgba(0,0,0,.07)', text:'#2D1B35',
    muted:'rgba(80,60,100,.55)', div:'rgba(0,0,0,.06)',
    navHover:'rgba(0,0,0,.04)', navActive:`${acc}12`,
    profileBg:`${acc}08`, profileBorder:`${acc}25`,
    cardShadow:'rgba(100,60,120,.12)',
  };

  const letter = (user?.first_name?.[0]||user?.username?.[0]||'?').toUpperCase();
  const name   = user?.first_name?`${user.first_name} ${user.last_name||''}`.trim():user?.username;
  const role   = user?.role==='superadmin'?'Суперадмин':user?.role==='admin'?'Тренер':'Клиент';
  const isAct  = to => location.pathname===to||(to!=='/dashboard'&&location.pathname.startsWith(to));

  return (
    <div style={{
      display:'flex', flexDirection:'column', width:230, height:'100%',
      background:T.bg, borderRight:`1px solid ${T.border}`,
      padding:'20px 14px', boxSizing:'border-box', flexShrink:0,
    }}>
      <style>{CSS}
        {`:root{--acc:${acc};--acc-glow:${glow};--nav-hover:${T.navHover};--nav-active:${T.navActive};--card-shadow:${T.cardShadow}}`}
      </style>

      {/* Logo */}
      <Link to="/dashboard" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none', marginBottom:22 }}>
        <div className="_logo-glow" style={{
          width:38, height:38, borderRadius:12, background:grad, flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <span style={{ color:'#fff', fontFamily:'Nunito,system-ui', fontWeight:900, fontSize:14, letterSpacing:-1 }}>MS</span>
        </div>
        <span style={{ color:T.text, fontFamily:'Nunito,system-ui', fontWeight:800, fontSize:'1.05rem', letterSpacing:-.5 }}>MolShape</span>
      </Link>

      {/* Profile */}
      <Link to="/profile" onClick={onClose} style={{
        display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:14,
        textDecoration:'none', marginBottom:18,
        background:T.profileBg, border:`1.5px solid ${T.profileBorder}`,
        transition:'border-color .2s',
      }}>
        <div style={{
          width:38, height:38, borderRadius:'50%', flexShrink:0,
          background:grad, display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:`0 4px 12px ${glow}`,
        }}>
          <span style={{ color:'#fff', fontWeight:700, fontSize:15 }}>{letter}</span>
        </div>
        <div style={{ minWidth:0 }}>
          <p style={{ color:T.text, fontSize:'.82rem', fontWeight:600, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</p>
          <p style={{ color:acc, fontSize:'.7rem', margin:0, fontWeight:500 }}>{role}</p>
        </div>
        {onClose&&<button onClick={e=>{e.preventDefault();onClose();}} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:T.muted,fontSize:20,lineHeight:1,padding:'0 2px'}}>×</button>}
      </Link>

      <div style={{ height:1, background:T.div, marginBottom:14 }}/>

      {/* Nav */}
      <nav style={{ flex:1, display:'flex', flexDirection:'column', gap:3 }}>
        {NAV.map(({ to, label, icon }) => {
          const active = isAct(to);
          return (
            <NavLink key={to} to={to} onClick={onClose}
              className={`_nav-item${active?' _nav-active':''}`}
              style={{
                display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                borderRadius:11, textDecoration:'none', fontSize:'.875rem',
                color: active ? acc : T.muted,
              }}>
              <span style={{ fontSize:'1rem', width:20, textAlign:'center', opacity:active?1:.7 }}>{icon}</span>
              {label}
            </NavLink>
          );
        })}
        {isAdmin && (() => {
          const active = location.pathname.startsWith('/admin');
          return (
            <NavLink to="/admin" onClick={onClose}
              className={`_nav-item${active?' _nav-active':''}`}
              style={{
                display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                borderRadius:11, textDecoration:'none', fontSize:'.875rem',
                color: active ? '#D97706' : T.muted,
                ...(active?{background:'rgba(217,119,6,.1)',borderLeftColor:'#D97706'}:{}),
              }}>
              <span style={{ fontSize:'1rem', width:20, textAlign:'center' }}>✦</span>
              Панель тренера
            </NavLink>
          );
        })()}
      </nav>

      {/* Color picker */}
      <div style={{ marginTop:16, marginBottom:14 }}>
        <p style={{ color:T.muted, fontSize:'.62rem', textTransform:'uppercase', letterSpacing:'.1em', margin:'0 0 9px 2px' }}>
          Тема
        </p>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {Object.values(THEMES).map(t => (
            <button key={t.key} onClick={()=>setColor(t.key)} title={t.name} className="_color-btn" style={{
              width:22, height:22, borderRadius:'50%', border:'none', cursor:'pointer',
              background:t.accent, padding:0,
              outline:`3px solid ${theme.key===t.key?t.accent:'transparent'}`,
              outlineOffset:2,
              boxShadow:theme.key===t.key?`0 0 12px ${t.glow}`:'none',
            }}/>
          ))}
        </div>
      </div>

      <div style={{ height:1, background:T.div, marginBottom:10 }}/>

      {/* Bottom */}
      {[
        { label:isDark?'☀️  Светлая тема':'🌙  Тёмная тема', action:toggleDark },
        { label:'→  Выйти', action:()=>{logout();navigate('/login');}, red:true },
      ].map(({label,action,red})=>(
        <button key={label} onClick={action} style={{
          display:'flex', alignItems:'center', padding:'8px 12px', borderRadius:10,
          background:'none', border:'none', cursor:'pointer', fontSize:'.82rem',
          color:red?'rgba(220,70,70,.75)':T.muted, width:'100%', textAlign:'left',
          marginBottom:2, transition:'opacity .15s',
        }}>{label}</button>
      ))}
      <a href="/privacy" style={{display:'block',padding:'5px 12px',fontSize:'.65rem',opacity:.4,textDecoration:'none',marginTop:4,color:'inherit'}}>
        Политика конфиденциальности · 152-ФЗ
      </a>
    </div>
  );
}

export default function Layout({ children }) {
  const { isDark, theme } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const [isDesk, setIsDesk] = useState(()=>window.innerWidth>=768);
  const [mobile, setMobile] = useState(false);
  const [key,    setKey]    = useState(location.pathname);

  useEffect(()=>{
    const h=()=>setIsDesk(window.innerWidth>=768);
    window.addEventListener('resize',h);
    return ()=>window.removeEventListener('resize',h);
  },[]);
  useEffect(()=>{ setKey(location.pathname); setMobile(false); },[location.pathname]);

  const bg   = isDark ? '#0E0B18' : '#F9F6FF';
  const topBg= isDark ? 'rgba(13,11,26,.96)' : 'rgba(253,251,255,.96)';
  const bdr  = isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.07)';
  const txt  = isDark ? '#F2EEF9' : '#2D1B35';
  const dot  = isDark ? 'rgba(255,255,255,.018)' : 'rgba(100,60,140,.025)';
  const letter=(user?.first_name?.[0]||user?.username?.[0]||'?').toUpperCase();

  return (
    <div style={{ display:'flex', height:'100vh', background:bg, overflow:'hidden', color:txt,
                  fontFamily:'Inter,system-ui,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Inter:wght@400;500;600&display=swap');
        @keyframes _glow{0%,100%{box-shadow:0 0 14px ${theme.glow}}50%{box-shadow:0 0 28px ${theme.glow}}}
        @keyframes _in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        ._page{animation:_in .35s cubic-bezier(.16,1,.3,1) both}
        ._logo-glow{animation:_glow 3s ease-in-out infinite}
        ._nav-item{transition:all .15s ease;border-left:3px solid transparent;display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:11px;text-decoration:none;font-size:.875rem}
        ._nav-item:hover{background:${isDark?'rgba(255,255,255,.05)':'rgba(0,0,0,.04)'}!important;padding-left:15px!important}
        ._nav-active{background:${theme.accent}18!important;border-left-color:${theme.accent}!important;color:${theme.accent}!important;font-weight:700!important}
        ._card{transition:transform .2s,box-shadow .2s}
        ._card:hover{transform:translateY(-2px)}
        ._btn{transition:all .2s;position:relative;overflow:hidden}
        ._btn:hover{transform:translateY(-1px)}
        ._btn:active{transform:scale(.97)}
        *{box-sizing:border-box}
      `}</style>

      {/* Desktop sidebar — rendered ONCE via JS */}
      {isDesk && <Sidebar/>}

      {/* Mobile overlay */}
      {!isDesk && mobile && (
        <div style={{ position:'fixed', inset:0, zIndex:60, display:'flex' }}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.6)', backdropFilter:'blur(6px)' }}
               onClick={()=>setMobile(false)}/>
          <div style={{ position:'relative', zIndex:1 }}>
            <Sidebar onClose={()=>setMobile(false)}/>
          </div>
        </div>
      )}

      <main style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflow:'hidden' }}>
        {/* Mobile top bar */}
        {!isDesk && (
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px',
                        background:topBg, borderBottom:`1px solid ${bdr}`,
                        backdropFilter:'blur(12px)', flexShrink:0 }}>
            <button onClick={()=>setMobile(true)} style={{ background:'none', border:'none',
                  cursor:'pointer', fontSize:20, color:txt, padding:'2px 4px', lineHeight:1 }}>☰</button>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div className="_logo-glow" style={{ width:28, height:28, borderRadius:8,
                    background:theme.grad, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ color:'#fff', fontWeight:900, fontSize:11, fontFamily:'Nunito,system-ui' }}>MS</span>
              </div>
              <span style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:800 }}>MolShape</span>
            </div>
            <div style={{ marginLeft:'auto', width:30, height:30, borderRadius:'50%',
                          background:theme.grad, display:'flex', alignItems:'center', justifyContent:'center',
                          boxShadow:`0 2px 8px ${theme.glow}` }}>
              <span style={{ color:'#fff', fontWeight:700, fontSize:12 }}>{letter}</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div key={key} className="_page" style={{
          flex:1, overflowY:'auto', padding:'1.5rem',
          backgroundImage:`radial-gradient(${dot} 1px, transparent 1px)`,
          backgroundSize:'28px 28px',
        }}>
          {children}
        </div>

        {/* Version */}
        <div style={{ position:'fixed', bottom:8, right:10, zIndex:50, pointerEvents:'none',
                      background:'rgba(0,0,0,.4)', backdropFilter:'blur(4px)',
                      border:'1px solid rgba(255,255,255,.06)', borderRadius:6,
                      padding:'2px 7px', fontSize:9, color:'rgba(255,255,255,.2)', fontFamily:'monospace' }}>
          {VER}
        </div>
      </main>
    </div>
  );
}
